import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { TourConfirmationType } from '@zg-rentals/ts-z-calendar-client';

import { trackEvent } from '../index';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

export const EVENT_IDS = {
  REVIEW_STEP_EDIT_BOOKING_METHOD_CLICK: '8067',
  REVIEW_STEP_BOOKING_METHOD_IMPRESSION: '8068',
  REVIEW_STEP_EDIT_AVAILABILITY_CLICK: '8069',
  REVIEW_STEP_EDIT_AVAILABILITY_IMPRESSION: '8070',
  REVIEW_STEP_BOOKING_METHOD_INITIAL_STATE_IMPRESSION: '8071',
  REVIEW_STEP_TOUR_DURATION_IMPRESSION: '8624',
  REVIEW_STEP_TOUR_DURATION_CLICK: '8629',
} as const;

const getTriggerLocation = (isActive: boolean) => {
  return isActive ? 'posting_path_review|active_listing' : 'posting_path_review|off_market_listing';
};

// https://dataportal.zillowgroup.net/event-registry/8067
export const emitReviewStepEditBookingMethodClickEvent = ({
  currentProperty,
  user,
  isActive,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  isActive: boolean;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.REVIEW_STEP_EDIT_BOOKING_METHOD_CLICK,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: getTriggerLocation(isActive),
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'tour_type_review',
    },
    semantic: {
      semantic_event_nm: 'edit_tour_type_listing_path',
      topic_tag_txt: ['zrm_properties', 'zrm_tour_scheduling'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8068
export const emitReviewStepBookingMethodImpressionEvent = ({
  currentProperty,
  user,
  isActive,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  isActive: boolean;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.REVIEW_STEP_BOOKING_METHOD_IMPRESSION,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: getTriggerLocation(isActive),
      trigger_type_nm: 'impression',
      trigger_object_nm: 'tour_type_question',
      trigger_source_nm: 'posting_path_review',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_tour_scheduling', 'zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8069
export const emitReviewStepEditAvailabilityClickEvent = ({
  currentProperty,
  user,
  isActive,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  isActive: boolean;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.REVIEW_STEP_EDIT_AVAILABILITY_CLICK,
      event_type_version_id: '3',
    },
    clickstream_trigger: {
      trigger_location_nm: getTriggerLocation(isActive),
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'tour_availability_updated',
    },
    semantic: {
      semantic_event_nm: 'edit_tour_availability_listing_path',
      topic_tag_txt: ['zrm_tour_scheduling', 'zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8070
export const emitReviewStepEditAvailabilityImpressionEvent = ({
  currentProperty,
  user,
  isActive,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  isActive: boolean;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.REVIEW_STEP_EDIT_AVAILABILITY_IMPRESSION,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: getTriggerLocation(isActive),
      trigger_type_nm: 'impression',
      trigger_object_nm: 'tour_availability_question',
      trigger_source_nm: 'posting_path_review',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_tour_scheduling', 'zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

const getReviewStepBookingMethodInitialStateImpressionTriggerSource = (tourConfirmationType?: TourConfirmationType) => {
  if (tourConfirmationType === TourConfirmationType.INSTANT) {
    return 'tour_type_instant';
  }

  if (tourConfirmationType === TourConfirmationType.LANDLORD_CONFIRM) {
    return 'tour_type_review';
  }

  return 'tour_scheduling_off';
};

// https://dataportal.zillowgroup.net/event-registry/8071
export const emitReviewStepBookingMethodInitialStateImpressionEvent = ({
  currentProperty,
  user,
  isActive,
  tourConfirmationType,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  isActive: boolean;
  tourConfirmationType?: TourConfirmationType;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.REVIEW_STEP_BOOKING_METHOD_INITIAL_STATE_IMPRESSION,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: getTriggerLocation(isActive),
      trigger_type_nm: 'impression',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: getReviewStepBookingMethodInitialStateImpressionTriggerSource(tourConfirmationType),
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_properties', 'zrm_tour_scheduling'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8624
export const emitReviewStepTourDurationImpressionEvent = ({
  currentProperty,
  user,
  isActive,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  isActive: boolean;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.REVIEW_STEP_TOUR_DURATION_IMPRESSION,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: getTriggerLocation(isActive),
      trigger_type_nm: 'impression',
      trigger_object_nm: 'tour_duration_question',
      trigger_source_nm: 'posting_path_review',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_properties', 'zrm_tour_scheduling'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8629
export const emitReviewStepTourDurationClickEvent = ({
  currentProperty,
  user,
  isActive,
  action,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  isActive: boolean;
  action?: string;
}): void =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.REVIEW_STEP_TOUR_DURATION_CLICK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: getTriggerLocation(isActive),
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: action === 'save' ? 'tour_duration_save' : 'tour_duration_cancel',
    },
    semantic: {
      semantic_event_nm: 'edit_tour_duration_listing_path',
      topic_tag_txt: ['zrm_properties', 'zrm_tour_scheduling'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });
